import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { money, formatDateAT } from "@/lib/format";
import { ExternalLink, LifeBuoy, PauseCircle, PlayCircle, Loader2 } from "lucide-react";

export default function AdminTenants() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const { startSupport } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/admin/tenants");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.brand_name || "").toLowerCase().includes(s) ||
      (r.owner_email || "").toLowerCase().includes(s) ||
      (r.slug || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const suspend = async (r) => {
    const reason = window.prompt(`Suspend ${r.name}? Optional reason:`, "");
    if (reason === null) return;
    setBusyId(r.id);
    try { await api.post(`/admin/tenants/${r.id}/suspend`, { reason }); await load(); toast.success("Suspended"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };
  const reactivate = async (r) => {
    setBusyId(r.id);
    try { await api.post(`/admin/tenants/${r.id}/reactivate`); await load(); toast.success("Reactivated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };
  const enterSupport = async (r) => {
    setBusyId(r.id);
    try {
      await startSupport(r.id);
      toast.success(`Support mode: ${r.name}`);
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  if (!items) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-tenants">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold font-display tracking-tight flex-1">Tenants <span className="text-sm text-muted-foreground font-mono">({items.length})</span></h1>
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / email / slug"
          className="max-w-xs" data-testid="tenants-search"
        />
      </div>

      <div className="border rounded-xl overflow-x-auto bg-background">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Restaurant</th>
              <th className="text-left px-3 py-2">Owner</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Users</th>
              <th className="text-right px-3 py-2">Captures (M)</th>
              <th className="text-right px-3 py-2">Rev (M)</th>
              <th className="text-right px-3 py-2">Exp (M)</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`tenant-row-${r.id}`}>
                <td className="px-3 py-2">
                  <Link to={`/admin/tenants/${r.id}`} className="hover:underline font-medium">
                    {r.brand_name || r.name}
                  </Link>
                  <div className="text-[11px] text-muted-foreground font-mono">{formatDateAT(r.created_at)}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-xs font-mono">{r.owner_email || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">{r.owner_last_login ? "last " + formatDateAT(r.owner_last_login) : "never"}</div>
                </td>
                <td className="px-3 py-2">
                  {r.status === "suspended"
                    ? <Badge className="bg-destructive hover:bg-destructive">Suspended</Badge>
                    : <Badge variant="outline" className="border-emerald-300 text-emerald-700">Active</Badge>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{r.users_count}</td>
                <td className="px-3 py-2 text-right font-mono">{r.ai_capture_count_month}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.revenue_month)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.expenses_month)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => nav(`/admin/tenants/${r.id}`)} className="h-7 px-2" data-testid={`tenant-view-${r.id}`}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => enterSupport(r)} disabled={busyId === r.id} className="h-7 px-2 gap-1" data-testid={`tenant-support-${r.id}`}>
                      {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LifeBuoy className="w-3.5 h-3.5" />}
                      Support
                    </Button>
                    {r.status === "suspended"
                      ? <Button size="sm" variant="outline" onClick={() => reactivate(r)} disabled={busyId === r.id} className="h-7 px-2" data-testid={`tenant-reactivate-${r.id}`}><PlayCircle className="w-3.5 h-3.5" /></Button>
                      : <Button size="sm" variant="outline" onClick={() => suspend(r)} disabled={busyId === r.id} className="h-7 px-2" data-testid={`tenant-suspend-${r.id}`}><PauseCircle className="w-3.5 h-3.5" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
